import { useMemo } from 'react';
import * as yup from 'yup';
import translation from 'i18n/translation.json';
import { GARegisterEvent, getLengthError, normalizePhoneDigitsForCompare } from 'utils';
import type { UserInfo } from 'interfaces';

const contactNamePattern = /^[a-zA-ZÁáÉéÍíÓóÚúÜüÑñ]+(( |-|\. ?)[a-zA-ZÁáÉéÍíÓóÚúÜüÑñ]+)*$/imu;
export const directMatchErrorMsg = 'El número de contacto adicional debe ser distinto al principal';

export const useAdditionalContactSchema = (userInfo: UserInfo | null) => {
  const registration = userInfo?.registration_type;
  const stage = 'confirmation_conditions';

  const schema = useMemo(
    () =>
      yup.object({
        additional_phone_number: yup
          .string()
          .trim()
          .required(() => {
            GARegisterEvent({
              event: 'error_data',
              label: 'sign_up',
              registration,
              stage,
              field_name: 'additional_phone_number',
              error_text: 'error_frontend: Contact phone number is required',
              UID: userInfo?.user_uuid,
            });
            return translation.errors.requiredField;
          })
          .test(
            '9-digits-in-number',
            () => {
              GARegisterEvent({
                event: 'error_data',
                label: 'sign_up',
                field_name: 'additional_phone_number',
                stage,
                error_text: 'error_frontend: The phone number should contain 9 digits, without the country code',
              });
              return translation.errors.pnoneNumberLength;
            },
            value => {
              return [9].includes(value.replace(/ /g, '').length);
            },
          )
          .test(
            'starts-with-9',
            () => {
              GARegisterEvent({
                event: 'error_data',
                label: 'sign_up',
                stage,
                field_name: 'additional_phone_number',
                error_text: 'error_backend: The phone number should start with 9',
              });
              return translation.errors.invalidPhoneNumber;
            },
            value => {
              return value.startsWith('9');
            },
          )
          .test(
            'distinct-from-main-phone',
            () => {
              GARegisterEvent({
                event: 'error_data',
                label: 'sign_up',
                registration,
                stage,
                field_name: 'additional_phone_number',
                error_text: 'error_frontend: Additional contact phone must differ from main phone',
                UID: userInfo?.user_uuid,
              });
              return directMatchErrorMsg;
            },
            value => {
              const mainPhone = normalizePhoneDigitsForCompare(userInfo?.phone_number);
              if (!mainPhone) {
                return true;
              }
              return normalizePhoneDigitsForCompare(value) !== mainPhone;
            },
          ),
        additional_phone_name: yup
          .string()
          .trim()
          .transform(value => value?.replace(/\s+/g, ' '))
          .required(() => {
            GARegisterEvent({
              event: 'error_data',
              label: 'sign_up',
              registration,
              stage,
              field_name: 'additional_phone_name',
              error_text: 'error_frontend: Contact name is required',
              UID: userInfo?.user_uuid,
            });
            return translation.errors.requiredField;
          })
          .test(
            'contact-name-format',
            () => {
              GARegisterEvent({
                event: 'error_data',
                label: 'sign_up',
                registration,
                stage,
                field_name: 'additional_phone_name',
                error_text: 'error_frontend: Invalid contact name',
                UID: userInfo?.user_uuid,
              });
              return translation.errors.invalidMiddleNames;
            },
            value => {
              return contactNamePattern.test(value);
            },
          )
          .min(2, () => {
            GARegisterEvent({
              event: 'error_data',
              label: 'sign_up',
              registration,
              stage,
              field_name: 'additional_phone_name',
              error_text: 'error_frontend: This field must have at least 2 and no more than 100 characters',
              UID: userInfo?.user_uuid,
            });
            return getLengthError(translation.errors.length, 2, 100);
          })
          .max(100, () => {
            GARegisterEvent({
              event: 'error_data',
              label: 'sign_up',
              registration,
              stage,
              field_name: 'additional_phone_name',
              error_text: 'error_frontend: This field must have at least 2 and no more than 100 characters',
              UID: userInfo?.user_uuid,
            });
            return getLengthError(translation.errors.length, 2, 100);
          }),
      }),
    [registration, userInfo?.phone_number, userInfo?.user_uuid],
  );

  const defaultValue: yup.InferType<typeof schema> = {
    additional_phone_number: '',
    additional_phone_name: '',
  };

  return { schema, defaultValue };
};
